import React, { useRef } from 'react';
import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Button,
    HStack,
    Icon,
    Text,
} from '@chakra-ui/react';
import { AiOutlineWarning, AiOutlineDelete } from 'react-icons/ai';

/**
 * Diálogo PADRÃO de confirmação de exclusão — usar em TODAS as telas que
 * possuem botão de excluir, para manter mensagem e visual consistentes.
 *
 * Props:
 *   isOpen / onClose  → controle do diálogo (useDisclosure)
 *   onConfirm         → callback executado ao confirmar a exclusão
 *   title             → título do diálogo (ex.: "Excluir Contato")
 *   itemName          → nome do item destacado na mensagem (ex.: "João da Silva")
 *   description       → texto adicional opcional após a pergunta padrão
 *   confirmLabel      → rótulo do botão de confirmação (padrão: "Sim, excluir")
 *   isLoading         → estado de carregamento do botão confirmar
 *   warning           → true = destaca a description como caixa de alerta vermelha
 *                       (ex.: item em uso por outros registros)
 */
const ConfirmDeleteDialog = ({
    isOpen,
    onClose,
    onConfirm,
    title = 'Confirmar Exclusão',
    itemName = '',
    description = '',
    confirmLabel = 'Sim, excluir',
    isLoading = false,
    warning = false,
}) => {
    const cancelRef = useRef(null);

    return (
        <AlertDialog
            isOpen={isOpen}
            leastDestructiveRef={cancelRef}
            onClose={onClose}
            isCentered
            motionPreset="slideInBottom"
        >
            <AlertDialogOverlay backdropFilter="blur(4px)">
                <AlertDialogContent
                    bg="surface"
                    border="1px solid"
                    borderColor="borderPrimary"
                    color="textPrimary"
                    borderRadius="xl"
                >
                    <AlertDialogHeader
                        fontSize="lg"
                        fontWeight="900"
                        borderBottom="1px solid"
                        borderColor="borderPrimary"
                    >
                        <HStack spacing={2}>
                            <Icon as={AiOutlineWarning} color="danger" />
                            <Text>{title}</Text>
                        </HStack>
                    </AlertDialogHeader>
                    <AlertDialogBody py={5}>
                        <Text color="textSecondary">
                            Tem certeza que deseja excluir
                            {itemName ? (
                                <>
                                    {' '}
                                    <Text as="span" fontWeight="800" color="textPrimary">
                                        "{itemName}"
                                    </Text>
                                </>
                            ) : (
                                ' este item'
                            )}
                            ? Esta ação não pode ser desfeita.
                        </Text>
                        {description && (warning ? (
                            <HStack
                                spacing={2}
                                mt={3}
                                p={3}
                                bg="rgba(252,129,129,0.10)"
                                border="1px solid"
                                borderColor="rgba(252,129,129,0.40)"
                                borderRadius="lg"
                                align="flex-start"
                            >
                                <Icon as={AiOutlineWarning} color="#FC8181" boxSize="16px" mt="2px" flexShrink={0} />
                                <Text color="#FC8181" fontSize="sm" fontWeight="700">
                                    {description}
                                </Text>
                            </HStack>
                        ) : (
                            <Text color="textMuted" fontSize="sm" mt={2}>
                                {description}
                            </Text>
                        ))}
                    </AlertDialogBody>
                    <AlertDialogFooter borderTop="1px solid" borderColor="borderPrimary" gap={3}>
                        <Button
                            ref={cancelRef}
                            variant="ghost"
                            color="textSecondary"
                            _hover={{ bg: 'surfaceHover' }}
                            onClick={onClose}
                            size="sm"
                        >
                            Cancelar
                        </Button>
                        <Button
                            leftIcon={<AiOutlineDelete />}
                            bg="red.500"
                            color="white"
                            _hover={{ bg: 'red.600' }}
                            fontWeight="800"
                            onClick={onConfirm}
                            isLoading={isLoading}
                            size="sm"
                        >
                            {confirmLabel}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialogOverlay>
        </AlertDialog>
    );
};

export default ConfirmDeleteDialog;
